import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle } from "lucide-react";

const Partners = () => {
  const benefits = [
    "High-engagement audiences across major U.S. cities",
    "Multi-city reach through national artist tours and festivals",
    "Cross-generational visibility spanning diaspora communities",
    "Alignment with the 20% Giving Model for social impact",
    "Authenticity and trust built over two decades of cultural programming",
    "Access to loyal Nepali diaspora audiences not reachable through mainstream media",
  ];

  const partnerTypes = [
    {
      title: "Brand Sponsors",
      description:
        "Reach engaged, culturally connected audiences through strategic sponsorships at DTFEST, artist tours, and community events. Your brand appears alongside content that communities trust and celebrate.",
    },
    {
      title: "Community Organizations",
      description:
        "Collaborate with 3E Entertainment to amplify your community mission. We co-create programming that drives mutual impact and strengthens the fabric of Nepali diaspora communities.",
    },
    {
      title: "Media & Press",
      description:
        "Cover landmark cultural events and access compelling stories of heritage, identity, youth, and belonging. 3E produces content that resonates with diverse audiences nationally.",
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="partners-page"
    >
      {/* Hero */}
      <section className="partners-hero">
        <div className="ph-overlay" />
        <motion.div
          className="ph-content"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            Partner With a Cultural Movement
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            Join brands, organizations, and community leaders who believe in the
            power of culture to create meaningful connection and measurable impact.
          </motion.p>
        </motion.div>
      </section>

      {/* Why Partner */}
      <section className="why-section">
        <div className="p-container">
          <div className="why-layout">
            <motion.div
              className="why-text"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2>
                Cultural Relevance, Community Credibility, Social Impact
              </h2>
              <p>
                Partnering with 3E means aligning your brand with one of the
                most trusted platforms for Nepali cultural programming in the
                United States. Our partnerships go beyond logo placement — they
                create opportunities to reach a loyal, growing, and
                multi-generational diaspora audience through experiences rooted
                in authenticity and trust.
              </p>
              <p>
                Through our platform, partners gain access to high-engagement
                audiences, multi-city reach, cross-generational visibility, and
                alignment with our 20% Giving Model.
              </p>
            </motion.div>

            <motion.div
              className="why-benefits"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h3>What You Gain</h3>
              <ul className="benefits-list">
                {benefits.map((benefit, i) => (
                  <li key={i}>
                    <CheckCircle size={18} className="check-icon" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Partner Types */}
      <section className="partner-types-section">
        <div className="p-container">
          <div className="pt-header">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              viewport={{ once: true }}
            >
              Ways to Get Involved
            </motion.h2>
          </div>

          <div className="pt-grid">
            {partnerTypes.map((type, index) => (
              <motion.div
                key={type.title}
                className="pt-card"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.12 }}
                viewport={{ once: true }}
                whileHover={{ y: -6, transition: { duration: 0.3 } }}
              >
                <div className="pt-card-number">
                  {String(index + 1).padStart(2, "0")}
                </div>
                <h4>{type.title}</h4>
                <p>{type.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="partner-cta-section">
        <div className="p-container">
          <motion.div
            className="partner-cta-box"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="pcta-text">
              <h2>Ready to Build Something Together?</h2>
              <p>
                We are not offering sponsorships alone. We are inviting
                visionary partners to become part of a movement that preserves
                culture, empowers communities, and builds legacy.
              </p>
            </div>
            <Link to="/contact" className="pcta-btn">
              Get In Touch <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>

      <style>{`
        .partners-page { overflow-x: hidden; }

        .partners-hero {
          position: relative;
          min-height: 65vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: url("/images/R5BI6877.webp") center/cover no-repeat;
        }

        .ph-overlay {
          position: absolute;
          inset: 0;
          background: rgba(10, 10, 10, 0.82);
        }

        .ph-content {
          position: relative;
          z-index: 2;
          text-align: center;
          max-width: 800px;
          padding: 0 2rem;
        }


        .ph-content h1 {
          font-size: clamp(2.5rem, 5.5vw, 3.8rem);
          font-weight: 900;
          color: white;
          margin: 0 0 1.5rem;
          line-height: 1.1;
        }

        .ph-content p {
          font-size: 1.15rem;
          color: rgba(255, 255, 255, 0.85);
          line-height: 1.7;
          max-width: 580px;
          margin: 0 auto;
        }

        .p-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
        }


        /* Why Section */
        .why-section {
          padding: 7rem 0;
          background: #fff;
        }

        .why-layout {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 5rem;
          align-items: start;
        }

        .why-text h2 {
          font-size: clamp(2rem, 3.5vw, 2.6rem);
          font-weight: 900;
          color: #0a0a0a;
          margin: 0 0 1.25rem;
          line-height: 1.1;
        }

        .why-text p {
          font-size: 1rem;
          color: #555;
          line-height: 1.8;
          margin: 0 0 1rem;
        }

        .why-benefits h3 {
          font-size: 1.1rem;
          font-weight: 700;
          color: #0a0a0a;
          margin: 0 0 1.5rem;
          text-transform: uppercase;
          letter-spacing: 1px;
          font-size: 0.85rem;
        }

        .benefits-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .benefits-list li {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          font-size: 0.97rem;
          color: #444;
          line-height: 1.6;
        }

        .check-icon {
          color: #C72631;
          flex-shrink: 0;
          margin-top: 2px;
        }

        /* Partner Types */
        .partner-types-section {
          padding: 6rem 0;
          background: #f7f7f7;
        }

        .pt-header {
          text-align: center;
          max-width: 600px;
          margin: 0 auto 4rem;
        }

        .pt-header h2 {
          font-size: clamp(2rem, 4vw, 2.8rem);
          font-weight: 900;
          color: #0a0a0a;
          margin: 0;
          line-height: 1.1;
        }

        .pt-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1.5rem;
        }

        .pt-card {
          background: #fff;
          border: 1px solid #f0f0f0;
          border-radius: 16px;
          padding: 2.5rem 2rem;
          transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
          cursor: default;
          box-shadow: 0 2px 15px rgba(0, 0, 0, 0.04);
        }

        .pt-card:hover {
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
          border-color: #C72631;
        }

        .pt-card-number {
          font-size: 2.5rem;
          font-weight: 900;
          color: #C72631;
          opacity: 0.3;
          line-height: 1;
          margin-bottom: 1.25rem;
        }

        .pt-card:hover .pt-card-number {
          opacity: 1;
        }

        .pt-card h4 {
          font-size: 1.2rem;
          font-weight: 700;
          color: #0a0a0a;
          margin: 0 0 0.75rem;
        }

        .pt-card p {
          font-size: 0.95rem;
          color: #666;
          line-height: 1.7;
          margin: 0;
        }

        /* Partner CTA */
        .partner-cta-section {
          padding: 5rem 0;
          background: #fff;
        }

        .partner-cta-box {
          background: #0a0a0a;
          border-radius: 20px;
          padding: 3.5rem 4rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 3rem;
        }

        .pcta-text h2 {
          font-size: clamp(1.8rem, 3vw, 2.4rem);
          font-weight: 900;
          color: #fff;
          margin: 0 0 1rem;
          line-height: 1.1;
        }

        .pcta-text p {
          font-size: 1rem;
          color: rgba(255, 255, 255, 0.65);
          line-height: 1.75;
          margin: 0;
          max-width: 560px;
        }

        .pcta-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: #C72631;
          color: #fff;
          padding: 1rem 2rem;
          border-radius: 8px;
          font-size: 0.95rem;
          font-weight: 700;
          text-decoration: none;
          text-transform: uppercase;
          letter-spacing: 1px;
          transition: background 0.3s cubic-bezier(0.22, 1, 0.36, 1), transform 0.3s ease;
          white-space: nowrap;
          flex-shrink: 0;
        }

        .pcta-btn:hover {
          background: #a01f28;
          transform: translateY(-3px);
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .why-layout {
            grid-template-columns: 1fr;
            gap: 3rem;
          }

          .pt-grid {
            grid-template-columns: 1fr;
            max-width: 460px;
            margin: 0 auto;
          }

          .partner-cta-box {
            flex-direction: column;
            padding: 3rem 2rem;
            text-align: center;
          }

          .pcta-btn {
            width: 100%;
            justify-content: center;
          }
        }

        @media (max-width: 768px) {
          .why-section,
          .partner-types-section {
            padding: 4.5rem 0;
          }

          .p-container {
            padding: 0 1.5rem;
          }
        }
      `}</style>
    </motion.div>
  );
};

export default Partners;
